import {
  Component,
  FileBase,
  JsonFile,
  Project,
  SampleDir,
  SampleFile,
  Task,
} from "projen/lib";
import { BuildWorkflowOptions } from "projen/lib/build";
import { JobPermission } from "projen/lib/github/workflows-model";
import { NodeProject } from "projen/lib/javascript";
import { TurboRepoTask } from "./turbo-repo-task";
import {
  TurboRunContinue,
  TurboRunDryRun,
  TurboRunLogOrder,
  TurboRunLogPrefix,
  TurboRunOutputLogs,
} from "./turbo-run-enums";
import { GIT_BRANCH_NAME_ENV, GIT_BRANCH_NAME_ENV_VALUE } from "../git";

/*******************************************************************************
 *
 * Turbo Repo Config
 *
 ******************************************************************************/

export const ROOT_TURBO_TASK_NAME = "turbo:build";
export const ROOT_CI_TASK_NAME = "build:all";

export interface RemoteCacheOptions {
  /**
   * Local profile name to use when fetching the cache endpoint and token.
   */
  readonly profileName: string;

  /**
   * OIDC role to assume when fetching the cache endpoint and token.
   */
  readonly oidcRole: string;

  /**
   * Name for the params used to store the cache endpoint.
   */
  readonly endpointParamName: string;

  /**
   * Name for the params used to store the cache's API token.
   */
  readonly tokenParamName: string;

  /**
   * team name used in remote cache commands
   */
  readonly teamName: string;
}

/**
 * Options for {@link TurboRepo.activateBranchNameEnvVar}.
 *
 * The `tasks` field is required so callers explicitly name every task whose
 * cache hash should include `GIT_BRANCH_NAME`. Scoping the env to a small
 * set of consuming tasks (typically CDK `synth` / `package`) preserves
 * cross-branch cache hits for every other task in the monorepo — library
 * `compile` / `test` / `lint` / `package` runs no longer miss cache just
 * because the branch name changed.
 *
 * The call also populates `GIT_BRANCH_NAME` on the root `build:all` task env
 * so the named tasks hash a real value locally, matching CI.
 */
export interface ActivateBranchNameEnvVarOptions {
  /**
   * Tasks whose cache hash should include GIT_BRANCH_NAME. Each named task
   * gets the env added to its `env: []` array. Unknown task names throw at
   * synth time so typos surface immediately.
   *
   * Adding a task to this list is a deliberate cache-hit-rate tradeoff:
   * the task will miss cache on every branch switch. Only name tasks that
   * actually read `GIT_BRANCH_NAME` at runtime (e.g. CDK stack-name
   * generators that bake the branch into resource names).
   */
  readonly tasks: Array<string>;
}

/**
 * CLI flags appended to the `turbo run` invocation in the root `build:all`
 * task and the sibling `reset:all` task. These are command-line *run* flags —
 * distinct from the `turbo.json` config keys on {@link TurboRepoOptions}
 * (`ui`, `cacheDir`, `envMode`, `globalDependencies`,
 * `dangerouslyDisablePackageManagerCheck`), which are written to the config
 * file rather than passed on the command line.
 *
 * Every field defaults to the value currently hard-coded by the component, so
 * omitting `runOptions` reproduces the historical command strings exactly.
 *
 * Targets the `turbo run` surface for Turbo 2.9.18. Flags not modeled here
 * (profiling, experimental OpenTelemetry, operational) stay reachable via
 * {@link TurboRunOptions.additionalArgs}.
 */
export interface TurboRunOptions {
  /**
   * `--concurrency` — limit concurrent task execution (number or percentage).
   *
   * @default 10
   */
  readonly concurrency?: string | number;

  /**
   * `--summarize` — write a run summary under `.turbo/runs`.
   *
   * @default true
   */
  readonly summarize?: boolean;

  /**
   * `--cache` — cache behavior, e.g. `"local:rw"` or `"remote:rw"`. Defaults to
   * `"remote:rw"` when a {@link RemoteCacheOptions} is configured; otherwise no
   * `--cache` flag is emitted.
   */
  readonly cache?: string;

  /**
   * `--force` — ignore the existing cache and force execution.
   */
  readonly force?: boolean;

  /**
   * `--no-cache` — do not write task results to the cache.
   */
  readonly noCache?: boolean;

  /**
   * `--cache-workers` — number of concurrent cache operations.
   */
  readonly cacheWorkers?: number;

  /**
   * `--continue` — how execution proceeds when a task fails.
   */
  readonly continueOn?: TurboRunContinue;

  /**
   * `--only` — run only the specified tasks, not their dependencies.
   */
  readonly only?: boolean;

  /**
   * `--framework-inference` — toggle framework inference for tasks.
   */
  readonly frameworkInference?: boolean;

  /**
   * `--filter` — package selectors; one `--filter` flag is emitted per entry.
   */
  readonly filter?: Array<string>;

  /**
   * `--affected` — restrict to packages affected vs. the base branch.
   */
  readonly affected?: boolean;

  /**
   * `--output-logs` — task output logging mode.
   */
  readonly outputLogs?: TurboRunOutputLogs;

  /**
   * `--log-order` — ordering of task output.
   */
  readonly logOrder?: TurboRunLogOrder;

  /**
   * `--log-prefix` — task log prefixing mode.
   */
  readonly logPrefix?: TurboRunLogPrefix;

  /**
   * `--dry-run` — print what would run without executing.
   */
  readonly dryRun?: TurboRunDryRun;

  /**
   * Escape hatch: extra raw flags appended verbatim after all modeled flags,
   * e.g. `["--anon-profile=prof.json", "--experimental-otel-enabled"]`. Lets
   * any current or future `turbo run` flag be passed even when not modeled
   * above.
   */
  readonly additionalArgs?: Array<string>;
}

export interface TurboRepoOptions {
  /**
   * Version of turborepo to use.
   *
   * @default specified in versions file
   */
  readonly turboVersion?: string;

  /**
   * Extend from the root turbo.json to create specific configuration for a package using Package Configurations.
   *
   * The only valid value for extends is ["//"] to inherit configuration from the root turbo.json.
   * If extends is used in the root turbo.json, it will be ignored.
   *
   * https://turbo.build/repo/docs/reference/configuration#extends
   */
  readonly extends?: Array<string>;

  /**
   * A list of globs that you want to include in all task hashes. If any file matching these globs changes, all tasks will miss cache. Globs are relative to the location of turbo.json.
   *
   * By default, all files in source control in the Workspace root are included in the global hash.
   *
   * Globs must be in the repository's source control root. Globs outside of the repository aren't supported.
   *
   * https://turbo.build/repo/docs/reference/configuration#globaldependencies
   */
  readonly globalDependencies?: Array<string>;

  /**
   * A list of environment variables that you want to impact the hash of all tasks. Any change to these environment variables will cause all tasks to miss cache.
   *
   * For more on wildcard and negation syntax, see the env section.
   *
   * https://turbo.build/repo/docs/reference/configuration#globalenv
   */
  readonly globalEnv?: Array<string>;

  /**
   * A list of environment variables that you want to make available to tasks.
   * Using this key opts all tasks into Strict
   * Environment Variable Mode.
   *
   * Additionally, Turborepo has a built-in set of global passthrough variables
   * for common cases, like operating system environment variables. This
   * includes variables like HOME, PATH, APPDATA, SHELL, PWD, and more. The full
   * list can be found in the source code.
   *
   * Passthrough values do not contribute to hashes for caching
   *
   * If you want changes in these variables to cause cache misses, you will need
   * to include them in env or globalEnv.
   *
   * https://turbo.build/repo/docs/reference/configuration#globalpassthroughenv
   */
  readonly globalPassThroughEnv?: Array<string>;

  /**
   * @default "stream"
   *
   * Select a terminal UI for the repository.
   *
   * "tui" allows for viewing each log at once and interacting with the task.
   * "stream" outputs logs as they come in and is not interactive.
   *
   * https://turbo.build/repo/docs/reference/configuration#ui
   */
  readonly ui?: "tui" | "stream";

  /**
   * @default false
   *
   * Turborepo uses your repository's lockfile to determine caching behavior,
   * Package Graphs, and more. Because of this, we use the packageManager field
   * to help you stabilize your Turborepo.
   *
   * To help with incremental migration or in situations where you can't use
   * the packageManager field, you may use
   * --dangerously-disable-package-manager-check to opt out of this check and
   * assume the risks of unstable lockfiles producing unpredictable behavior.
   * When disabled, Turborepo will attempt a best-effort discovery of the
   * intended package manager meant for the repository.
   *
   * https://turbo.build/repo/docs/reference/configuration#dangerouslydisablepackagemanagercheck
   */
  readonly dangerouslyDisablePackageManagerCheck?: boolean;

  /**
   * @default ".turbo/cache"
   *
   * Specify the filesystem cache directory.
   *
   * https://turbo.build/repo/docs/reference/configuration#cachedir
   */
  readonly cacheDir?: string;

  /**
   * @deprecated The `daemon` configuration option is deprecated in Turbo 2.x
   * and will be removed in Turbo 3.0 — the daemon is no longer used for
   * `turbo run`. Setting this value has no effect; the key is not emitted
   * to `turbo.json`. Remove the option from your configulator config.
   *
   * https://turbo.build/repo/docs/reference/configuration#daemon
   */
  readonly daemon?: boolean;

  /**
   * @default "strict"
   *
   * Turborepo's Environment Modes allow you to control which environment
   * variables are available to a task at runtime:
   *
   *"strict": Filter environment variables to only those that are specified
   * in the env and globalEnv keys in turbo.json.
   *
   * "loose": Allow all environment variables for the process to be available.
   *
   * https://turbo.build/repo/docs/reference/configuration#envmode
   */
  readonly envMode?: string;

  /**
   * CLI flags for the `turbo run` invocation in the root `build:all` /
   * `reset:all` tasks. Defaults preserve the historically hard-coded flags
   * (`--summarize`, `--concurrency=10`, and the remote-cache flags), so
   * existing consumers are unaffected.
   *
   * @default - `--summarize --concurrency=10` (plus remote-cache flags when `remoteCacheOptions` is set)
   */
  readonly runOptions?: TurboRunOptions;

  /*****************************************************************************
   *
   * Cache Settings
   *
   ****************************************************************************/

  /**
   * Cache settings, if using remote cache.
   */
  readonly remoteCacheOptions?: RemoteCacheOptions;

  /**
   * Env Args that will bre added to turbo's build:all task
   *
   * @default `{}`
   */
  readonly buildAllTaskEnvVars?: Record<string, string>;

  /*****************************************************************************
   *
   * Tasks - Optionally define a different projen task for one of the lifecycle
   * steps.
   *
   ****************************************************************************/

  /**
   * Pre compile task
   *
   * @default "pre-compile"
   */
  readonly preCompileTask?: Task;

  /**
   * Compile task
   *
   * @default "compile"
   */
  readonly compileTask?: Task;

  /**
   * Post compile task
   *
   * @default "post-compile"
   */
  readonly postCompileTask?: Task;

  /**
   * Test task
   *
   * @default "test"
   */
  readonly testTask?: Task;

  /**
   * Package task
   *
   * @default "package"
   */
  readonly packageTask?: Task;
}

export class TurboRepo extends Component {
  /**
   * Static method to discovert turbo in a project.
   */
  public static of(project: Project): TurboRepo | undefined {
    const isDefined = (c: Component): c is TurboRepo => c instanceof TurboRepo;
    return project.components.find(isDefined);
  }

  public static buildWorkflowOptions = (
    remoteCacheOptions: RemoteCacheOptions,
  ): Partial<BuildWorkflowOptions> => {
    return {
      env: {
        GIT_BRANCH_NAME: "${{ github.head_ref || github.ref_name }}",
      },
      permissions: {
        contents: JobPermission.WRITE,
        idToken: JobPermission.WRITE,
      },
      preBuildSteps: [
        {
          name: "AWS Creds for SSM",
          uses: "aws-actions/configure-aws-credentials@v6",
          with: {
            ["role-to-assume"]: remoteCacheOptions.oidcRole,
            ["aws-region"]: "us-east-1",
            ["role-duration-seconds"]: "900",
          },
        },
      ],
    };
  };

  /**
   * Version of turborepo to use.
   */
  public readonly turboVersion: string;

  /**
   * Extend from the root turbo.json to create specific configuration for a package using Package Configurations.
   *
   * The only valid value for extends is ["//"] to inherit configuration from the root turbo.json.
   * If extends is used in the root turbo.json, it will be ignored.
   *
   * https://turbo.build/repo/docs/reference/configuration#extends
   */
  public readonly extends: Array<string>;

  /**
   * A list of globs that you want to include in all task hashes. If any file matching these globs changes, all tasks will miss cache. Globs are relative to the location of turbo.json.
   *
   * By default, all files in source control in the Workspace root are included in the global hash.
   *
   * Globs must be in the repository's source control root. Globs outside of the repository aren't supported.
   *
   * https://turbo.build/repo/docs/reference/configuration#globaldependencies
   */
  public readonly globalDependencies: Array<string>;

  /**
   * A list of environment variables that you want to impact the hash of all tasks. Any change to these environment variables will cause all tasks to miss cache.
   *
   * For more on wildcard and negation syntax, see the env section.
   *
   * https://turbo.build/repo/docs/reference/configuration#globalenv
   */
  public readonly globalEnv: Array<string>;

  /**
   * A list of environment variables that you want to make available to tasks.
   * Using this key opts all tasks into Strict
   * Environment Variable Mode.
   *
   * Additionally, Turborepo has a built-in set of global passthrough variables
   * for common cases, like operating system environment variables. This
   * includes variables like HOME, PATH, APPDATA, SHELL, PWD, and more. The full
   * list can be found in the source code.
   *
   * Passthrough values do not contribute to hashes for caching
   *
   * If you want changes in these variables to cause cache misses, you will need
   * to include them in env or globalEnv.
   *
   * https://turbo.build/repo/docs/reference/configuration#globalpassthroughenv
   */
  public readonly globalPassThroughEnv: Array<string>;

  /**
   * @default "stream"
   *
   * Select a terminal UI for the repository.
   *
   * "tui" allows for viewing each log at once and interacting with the task.
   * "stream" outputs logs as they come in and is not interactive.
   *
   * https://turbo.build/repo/docs/reference/configuration#ui
   */
  public readonly ui: "tui" | "stream";

  /**
   * @default false
   *
   * Turborepo uses your repository's lockfile to determine caching behavior,
   * Package Graphs, and more. Because of this, we use the packageManager field
   * to help you stabilize your Turborepo.
   *
   * To help with incremental migration or in situations where you can't use
   * the packageManager field, you may use
   * --dangerously-disable-package-manager-check to opt out of this check and
   * assume the risks of unstable lockfiles producing unpredictable behavior.
   * When disabled, Turborepo will attempt a best-effort discovery of the
   * intended package manager meant for the repository.
   *
   * https://turbo.build/repo/docs/reference/configuration#dangerouslydisablepackagemanagercheck
   */
  public readonly dangerouslyDisablePackageManagerCheck: boolean;

  /**
   * @default ".turbo/cache"
   *
   * Specify the filesystem cache directory.
   *
   * https://turbo.build/repo/docs/reference/configuration#cachedir
   */
  public readonly cacheDir: string;

  /**
   * @deprecated The `daemon` configuration option is deprecated in Turbo 2.x
   * and will be removed in Turbo 3.0 — the daemon is no longer used for
   * `turbo run`. Reading this property returns whatever was passed to the
   * constructor (default `true`), but the value is never written to
   * `turbo.json`. New code should not consult this property.
   */
  public readonly daemon: boolean;

  /**
   * @default "strict"
   *
   * Turborepo's Environment Modes allow you to control which environment
   * variables are available to a task at runtime:
   *
   *"strict": Filter environment variables to only those that are specified
   * in the env and globalEnv keys in turbo.json.
   *
   * "loose": Allow all environment variables for the process to be available.
   *
   * https://turbo.build/repo/docs/reference/configuration#envmode
   */
  public readonly envMode: string;

  /**
   * Resolved `turbo run` CLI flags, with historical defaults applied. Rendered
   * into the `build:all` / `reset:all` task commands via {@link renderRunArgs}.
   */
  public readonly runOptions: TurboRunOptions;

  /*****************************************************************************
   *
   * Cache Settings
   *
   ****************************************************************************/

  /**
   * Cache settings, if using remote cache.
   */
  public readonly remoteCacheOptions?: RemoteCacheOptions;

  /**
   * is this the root project?
   */
  public readonly isRootProject: boolean;

  /**
   * Turbo's build:all task at the root of the monorepo.
   *
   * This is a normal projen task that runs the root turbo task.
   */
  public readonly buildAllTask?: Task;

  /**
   * Main turbo:build task
   *
   * This is a special Turbo task
   */
  public readonly buildTask: TurboRepoTask;

  /**
   * pre compile task
   */
  public readonly preCompileTask?: TurboRepoTask;

  /**
   * compile task
   */
  public readonly compileTask?: TurboRepoTask;

  /**
   * post compile task
   */
  public readonly postCompileTask?: TurboRepoTask;

  /**
   * Test task
   */
  public readonly testTask?: TurboRepoTask;

  /**
   * Package task
   */
  public readonly packageTask?: TurboRepoTask;

  /**
   * Sub-Tasks to run
   */
  public readonly tasks: Array<TurboRepoTask> = [];

  /**
   * Env Args that will bre added to turbo's build:all task
   */
  public readonly buildAllTaskEnvVars: Record<string, string>;

  constructor(
    public readonly project: NodeProject,
    options: TurboRepoOptions = {},
  ) {
    super(project);

    this.turboVersion = options.turboVersion ?? "catalog:";
    this.isRootProject = project === project.root;

    /**
     * Add turborepo package to root project.
     */
    if (this.isRootProject) {
      project.addDevDeps(`turbo@${this.turboVersion}`);
    }

    /**
     * Ignore the working cache for turbo.
     */
    project.gitignore.addPatterns("/.turbo");
    project.npmignore?.addPatterns("/.turbo/");

    /***************************************************************************
     *
     * Set some default options
     *
     **************************************************************************/

    this.extends = options.extends ?? (this.isRootProject ? [] : ["//"]);
    this.globalDependencies = options.globalDependencies ?? [];
    this.globalEnv = options.globalEnv ?? [];
    this.globalPassThroughEnv = options.globalPassThroughEnv ?? [];
    this.ui = options.ui ?? "stream";
    this.dangerouslyDisablePackageManagerCheck =
      options.dangerouslyDisablePackageManagerCheck ?? false;
    this.cacheDir = options.cacheDir ?? ".turbo/cache";
    this.daemon = options.daemon ?? true;
    this.envMode = options.envMode ?? "strict";
    this.runOptions = {
      ...options.runOptions,
      summarize: options.runOptions?.summarize ?? true,
      concurrency: options.runOptions?.concurrency ?? 10,
    };
    this.remoteCacheOptions = options.remoteCacheOptions;
    this.buildAllTaskEnvVars = options.buildAllTaskEnvVars ?? {};

    /***************************************************************************
     *
     * Turbo Build Task
     *
     * All turbo configs contain a turbo build task.
     *
     **************************************************************************/

    /**
     * The turbo entry point definition for the turbo:build task. This exists
     * in each project and is written tot he turbo.json file. This task is not
     * a projen task or a Node task.
     *
     * For the root project, generated file paths are added as inputs at synth
     * time (see {@link generatedFileInputs}, applied in {@link preSynthesize})
     * so that when projen regenerates files (e.g. turbo.json, workflows), the
     * root task cache is invalidated.
     */
    this.buildTask = new TurboRepoTask(this.project, {
      name: ROOT_TURBO_TASK_NAME,
      dependsOn: this.isRootProject ? [`^${ROOT_TURBO_TASK_NAME}`] : [],
    });

    /***************************************************************************
     *
     * Turbo Build All Task
     *
     * The Projen entry point. This only exists in the root and is a projen
     * task that can be run using pnpm.
     *
     * - Turns off telemetry
     * - Runs root turbo task to build all sub-projects.
     *
     **************************************************************************/

    if (this.isRootProject) {
      /**
       * Create and configure the build all task.
       */
      this.buildAllTask = this.project.tasks.addTask(ROOT_CI_TASK_NAME, {
        description:
          "Root build followed by sub-project builds. Mimics the CI build process in one step.",
      });
      this.buildAllTask.exec("turbo telemetry disable");

      /**
       * Any env vars that may need to be added to the build all task.
       */
      if (this.buildAllTaskEnvVars) {
        Object.entries(this.buildAllTaskEnvVars).forEach(([name, value]) => {
          this.addGlobalEnvVar(name, value);
        });
      }

      /**
       * No remote cache in use, so run the root turbo task to build all
       * sub-projects locally.
       */
      if (!this.remoteCacheOptions) {
        this.buildAllTask.exec(
          `turbo ${ROOT_TURBO_TASK_NAME} ${this.renderRunArgs(false)}`,
        );
      } else {
        /**
         * Remote cache in use, so run the root turbo task to build all
         * sub-projects using the remote cache.
         */
        this.buildAllTask.exec(
          `turbo ${ROOT_TURBO_TASK_NAME} ${this.renderRunArgs(true)}`,
          {
            condition: '[ ! -n "$CI" ]',
            env: {
              TURBO_ENDPOINT: `$(aws ssm get-parameter --name ${this.remoteCacheOptions.endpointParamName} --query Parameter.Value --output text --profile ${this.remoteCacheOptions.profileName})`,
              TURBO_TOKEN: `$(aws ssm get-parameter --name ${this.remoteCacheOptions.tokenParamName} --query Parameter.Value --output text --profile ${this.remoteCacheOptions.profileName})`,
            },
          },
        );
        // running in CI, We'll depend on OIDC auth.
        this.buildAllTask.exec(
          `turbo ${ROOT_TURBO_TASK_NAME} ${this.renderRunArgs(true)}`,
          {
            condition: '[ -n "$CI" ]',
            env: {
              TURBO_ENDPOINT: `$(aws ssm get-parameter --name ${this.remoteCacheOptions.endpointParamName} --query Parameter.Value --output text)`,
              TURBO_TOKEN: `$(aws ssm get-parameter --name ${this.remoteCacheOptions.tokenParamName} --query Parameter.Value --output text)`,
            },
          },
        );
      }
    }

    /**
     * SUB-PROJECT
     *
     * Creates tasks for the typical lifecycle for all project projects.
     */
    if (!this.isRootProject) {
      /**
       * Generated file paths (managed `FileBase` files plus generated-once
       * `SampleFile` / `SampleDir`) are appended to these compile-phase task
       * inputs at synth time — see {@link generatedFileInputs}, applied in
       * {@link preSynthesize} — so they are captured even when added after this
       * component is constructed (e.g. a subclass's SampleFiles).
       */
      this.preCompileTask = new TurboRepoTask(project, {
        name: options.preCompileTask?.name ?? "pre-compile",
        inputs: ["src/**"],
      });
      this.compileTask = new TurboRepoTask(project, {
        name: options.compileTask?.name ?? "compile",
        inputs: ["src/**"],
      });
      this.postCompileTask = new TurboRepoTask(project, {
        name: options.postCompileTask?.name ?? "post-compile",
        inputs: ["src/**"],
      });
      this.testTask = new TurboRepoTask(project, {
        name: options.testTask?.name ?? "test",
      });
      this.packageTask = new TurboRepoTask(project, {
        name: options.packageTask?.name ?? "package",
        inputs: [".npmignore"],
      });
      this.tasks.push(
        this.preCompileTask,
        this.compileTask,
        this.postCompileTask,
        this.testTask,
        this.packageTask,
      );
    }
  }

  /**
   * Render the `turbo run` CLI flag string for the `build:all` / `reset:all`
   * task commands from {@link runOptions}. With no `runOptions` configured the
   * output matches the historically hard-coded flags exactly.
   *
   * @param remote - when `true`, also emit the remote-cache flags
   * (`--cache=remote:rw` plus `--api` / `--token` / `--team` derived from
   * {@link remoteCacheOptions}). The remote-cache `build:all` variant passes
   * `true`; `reset:all` and the local `build:all` variant pass `false`.
   */
  public renderRunArgs(remote: boolean): string {
    const run = this.runOptions;
    const args: Array<string> = [];

    // --summarize / --concurrency carry the historical defaults applied in the
    // constructor, so the empty-runOptions case renders byte-identically.
    if (run.summarize) {
      args.push("--summarize");
    }
    args.push(`--concurrency=${run.concurrency}`);

    // Presence-only boolean flags.
    if (run.force) {
      args.push("--force");
    }
    if (run.noCache) {
      args.push("--no-cache");
    }
    if (run.only) {
      args.push("--only");
    }
    if (run.affected) {
      args.push("--affected");
    }

    // Value flags — emitted only when explicitly set.
    if (run.cacheWorkers !== undefined) {
      args.push(`--cache-workers=${run.cacheWorkers}`);
    }
    if (run.continueOn !== undefined) {
      args.push(`--continue=${run.continueOn}`);
    }
    if (run.frameworkInference !== undefined) {
      args.push(`--framework-inference=${run.frameworkInference}`);
    }
    for (const filter of run.filter ?? []) {
      args.push(`--filter=${filter}`);
    }
    if (run.outputLogs !== undefined) {
      args.push(`--output-logs=${run.outputLogs}`);
    }
    if (run.logOrder !== undefined) {
      args.push(`--log-order=${run.logOrder}`);
    }
    if (run.logPrefix !== undefined) {
      args.push(`--log-prefix=${run.logPrefix}`);
    }
    if (run.dryRun !== undefined) {
      args.push(`--dry-run=${run.dryRun}`);
    }

    /**
     * `--cache`: an explicit value always wins; otherwise default to
     * `remote:rw` only in the remote-cache branch (matching the historical
     * hard-coded command). The local branch emits no `--cache` by default.
     */
    const cache = run.cache ?? (remote ? "remote:rw" : undefined);
    if (cache !== undefined) {
      args.push(`--cache=${cache}`);
    }

    /**
     * Remote-cache auth flags are derived from `remoteCacheOptions`, never from
     * `runOptions`. The `$TURBO_ENDPOINT` / `$TURBO_TOKEN` references resolve
     * from the task's `env` at run time.
     */
    if (remote && this.remoteCacheOptions) {
      args.push(
        "--api=$TURBO_ENDPOINT",
        "--token=$TURBO_TOKEN",
        `--team=${this.remoteCacheOptions.teamName}`,
      );
    }

    // Escape hatch — appended verbatim, last, so it can extend or override.
    if (run.additionalArgs) {
      args.push(...run.additionalArgs);
    }

    return args.join(" ");
  }

  /**
   * Add an env var to the global env vars for all tasks.
   * This will also become an input for the build:all task cache at the root.
   */
  public addGlobalEnvVar(name: string, value: string) {
    /**
     * Add env var to global task env
     */
    this.buildAllTask?.env(name, value);

    /**
     * Add to global env vars for all tasks.
     */
    if (this.isRootProject) {
      this.globalEnv.push(name);
    }
  }

  /**
   * @deprecated Pass `{ tasks: [...] }` and name only the tasks whose cache
   * hash must include `GIT_BRANCH_NAME`. The no-args form writes the env to
   * the root `globalEnv` array, which forces every task in the monorepo to
   * miss cache on every branch switch — destroying cross-branch cache hits
   * for library `compile` / `test` / `lint` / `package` work that does not
   * read the branch name at runtime.
   */
  public activateBranchNameEnvVar(): void;
  /**
   * Scopes `GIT_BRANCH_NAME` to the named tasks on this TurboRepo.
   *
   * Two layers are wired, and both are required:
   *
   * 1. Each named task's `env: []` array gets `GIT_BRANCH_NAME`, telling turbo
   *    *to hash* the variable for that task. Only those tasks miss cache on a
   *    branch switch — other tasks and the root `globalEnv` are left untouched.
   * 2. The root `build:all` projen task env gets the value, so the variable is
   *    actually *populated* in the process turbo runs in. Without this, turbo
   *    hashes the declared variable as unset locally while CI hashes the job
   *    env's value, and the two can never share a cache entry.
   *
   * Layer 2 is deliberately written to the `build:all` task env rather than to
   * `globalEnv`: an unset-vs-set value in turbo's process env only changes the
   * hash of tasks that declare it, so cross-branch cache hits survive for
   * every task that does not.
   *
   * Throws at synth time when `options.tasks` contains a name that does
   * not match a task on this TurboRepo, so typos surface immediately.
   */
  public activateBranchNameEnvVar(
    options: ActivateBranchNameEnvVarOptions,
  ): void;
  public activateBranchNameEnvVar(
    options?: ActivateBranchNameEnvVarOptions,
  ): void {
    /**
     * Legacy no-args form. Preserved so existing consumers keep building
     * without code edits; emits a deprecation warning so the regression
     * risk surfaces in synth logs.
     */
    if (options === undefined) {
      this.project.logger.warn(
        "TurboRepo.activateBranchNameEnvVar() with no arguments is deprecated. " +
          "It writes GIT_BRANCH_NAME to the root `globalEnv`, which forces every " +
          "task in the monorepo to miss cache on every branch switch. Pass " +
          "`{ tasks: [...] }` and name only the tasks that actually consume the " +
          "branch (e.g. CDK synth/package) to preserve cross-branch cache hits " +
          "for everything else.",
      );
      this.addGlobalEnvVar(GIT_BRANCH_NAME_ENV, GIT_BRANCH_NAME_ENV_VALUE);
      return;
    }

    /**
     * Per-task scope. Validate every requested task name against this
     * TurboRepo's known tasks before mutating anything so a typo in one
     * entry cannot leave a partial state behind.
     */
    const knownTaskNames = this.tasks.map((task) => task.name);
    const unknown = options.tasks.filter(
      (name) => !knownTaskNames.includes(name),
    );
    if (unknown.length > 0) {
      throw new Error(
        `TurboRepo.activateBranchNameEnvVar: unknown task name(s) ${JSON.stringify(
          unknown,
        )}. Known tasks on this TurboRepo: ${JSON.stringify(knownTaskNames)}.`,
      );
    }

    for (const name of options.tasks) {
      const task = this.tasks.find((t) => t.name === name);
      // Guaranteed non-undefined by the validation pass above, but narrow for TS.
      if (task && !task.env.includes(GIT_BRANCH_NAME_ENV)) {
        task.env.push(GIT_BRANCH_NAME_ENV);
      }
    }

    /**
     * Declaring the env on a task only tells turbo to hash it — something has
     * to populate it in turbo's own process for the hash to carry a value.
     * Runs only after the validation pass above, so a typo leaves no partial
     * state behind at either layer.
     */
    this.injectBranchNameOnBuildAll();
  }

  /**
   * Populate `GIT_BRANCH_NAME` on the root `build:all` task env — the process
   * env turbo runs in — so locally computed hashes match CI, where the
   * workflow job env supplies the same variable.
   *
   * Written straight to the task rather than through
   * {@link addGlobalEnvVar}, which would also push the name onto the root
   * `globalEnv` and bust the cache for every task in the monorepo.
   *
   * No-ops when the root project has no TurboRepo (`turbo: false`) and
   * therefore no `build:all` task to inject into.
   */
  private injectBranchNameOnBuildAll(): void {
    const rootTurbo = TurboRepo.of(this.project.root);
    rootTurbo?.buildAllTask?.env(
      GIT_BRANCH_NAME_ENV,
      GIT_BRANCH_NAME_ENV_VALUE,
    );
  }

  /**
   * Paths of all generated files in the project, deduped, for use as task
   * inputs so the compile cache invalidates when they change. Includes
   * projen-managed `FileBase` files plus generated-once `SampleFile` /
   * `SampleDir` — whose paths projen keeps private, so they are read defensively
   * and skipped if that internal shape ever changes. Computed at synth time so
   * files added after this component (e.g. a subclass's SampleFiles) are seen.
   */
  private generatedFileInputs(): Array<string> {
    const inputs = new Set<string>();

    for (const component of this.project.components) {
      if (component instanceof FileBase) {
        inputs.add(component.path);
      } else if (component instanceof SampleFile) {
        const filePath = (component as unknown as { filePath?: unknown })
          .filePath;
        if (typeof filePath === "string") {
          inputs.add(filePath);
        }
      } else if (component instanceof SampleDir) {
        const dir = (component as unknown as { dir?: unknown }).dir;
        if (typeof dir === "string") {
          inputs.add(`${dir}/**`);
        }
      }
    }

    return Array.from(inputs);
  }

  preSynthesize(): void {
    /**
     * Add any local workspace specific deps to dependsOn so that we know
     * compile has finished before attempting to use any distribution artifacts.
     *
     * Do this in presynth so that we can be sure all project dependencies have
     * been defined first.
     */
    let nextDependsOn = this.project.deps.all
      .filter((d) => d.version === "workspace:*")
      .map((d) => [d.name, ROOT_TURBO_TASK_NAME].join("#"));

    /**
     * Loop over all projen tasks and their corresponding turbo task
     * definitions. Chain them together using dependsOn, along with any outside
     * package dependencies.
     */
    if (!this.isRootProject) {
      (
        [
          [this.project.preCompileTask, this.preCompileTask],
          [this.project.compileTask, this.compileTask],
          [this.project.postCompileTask, this.postCompileTask],
          [this.project.testTask, this.testTask],
          [this.project.packageTask, this.packageTask],
        ] as Array<[Task, TurboRepoTask]>
      ).forEach(([pjTask, turboTask]) => {
        /**
         * If steps exist chain using dependsOn
         */
        if (pjTask && turboTask && pjTask.steps.length > 0) {
          if (nextDependsOn.length > 0) {
            turboTask.dependsOn.push(...nextDependsOn);
          }
          nextDependsOn = [turboTask.name];

          /**
           * Otherwise, if a task doesn't do anything, deactivate it.
           */
        } else {
          turboTask.isActive = false;
        }
      });

      /**
       * Main build entry point depends on whatever was last.
       */
      this.buildTask.dependsOn.push(...nextDependsOn);
    }

    /**
     * Append generated-file paths to the compile-phase task inputs (or the root
     * build task) now that all files — including generated-once SampleFiles
     * added after this component — are registered. Done here, not in the
     * constructor, so those late-added inputs become part of the turbo cache
     * key. Deduped against existing inputs so any inputs a project type declared
     * explicitly are not duplicated.
     */
    const generatedInputs = this.generatedFileInputs();
    const appendGeneratedInputs = (task?: TurboRepoTask): void => {
      if (!task) {
        return;
      }
      for (const input of generatedInputs) {
        if (!task.inputs.includes(input)) {
          task.inputs.push(input);
        }
      }
    };

    if (this.isRootProject) {
      appendGeneratedInputs(this.buildTask);
    } else {
      appendGeneratedInputs(this.preCompileTask);
      appendGeneratedInputs(this.compileTask);
      appendGeneratedInputs(this.postCompileTask);
    }

    /**
     * The name for the turbo config file.
     */
    const fileName: string = "turbo.json";

    /**
     * Ensure that turbo config doesn't end up in any package distributions.
     */
    this.project.addPackageIgnore(fileName);

    /**
     * The content of this YAML file will be resolved at synth time. By then,
     * any sub-projects will be defined and this will be a complete list.
     */
    new JsonFile(this.project, fileName, {
      obj: {
        extends: this.extends.length ? this.extends : undefined,
        globalDependencies:
          this.isRootProject && this.globalDependencies.length
            ? this.globalDependencies
            : undefined,
        globalEnv:
          this.isRootProject && this.globalEnv.length
            ? this.globalEnv
            : undefined,
        globalPassThroughEnv:
          this.isRootProject && this.globalPassThroughEnv.length
            ? this.globalPassThroughEnv
            : undefined,
        ui: this.isRootProject ? this.ui : undefined,
        dangerouslyDisablePackageManagerCheck: this.isRootProject
          ? this.dangerouslyDisablePackageManagerCheck
          : undefined,
        cacheDir: this.isRootProject ? this.cacheDir : undefined,
        envMode: this.isRootProject ? this.envMode : undefined,
        /**
         * All tasks
         */
        tasks: this.tasks
          .filter((task) => task.isActive)
          .reduce(
            (acc, task) => {
              acc[task.name] = {
                ...task.taskConfig(),
              };
              return acc;
            },
            {
              [this.buildTask.name]: { ...this.buildTask.taskConfig() },
            } as Record<string, any>,
          ),
      },
    });

    super.preSynthesize();
  }
}
